import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { MediumCustomIcon } from "../../layer00";
import { ReactNode } from "react";

export type StyledTableProps = {
  rows: ReactNode | ReactNode[];
  headers: Array<string | ReactNode>;
  chevronColumns?: string[];
  mobileEdgeToEdge?: boolean;
  sortableColumns?: Record<string, string>;
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  onSort?: (sortValue: string, direction: "asc" | "desc") => void;
  columnWidths?: Record<string, string>;
};

export const StyledTable = (props: StyledTableProps) => {
  const edge = props.mobileEdgeToEdge;
  const { sortableColumns, sortBy, sortDirection = "asc", onSort, columnWidths } =
    props;

  const showChevron = (node: string | ReactNode, i: number) => {
    if (typeof node !== "string") return false;
    return props.chevronColumns ? props.chevronColumns.includes(node) : i === 0;
  };

  const handleSortClick = (sortValue: string) => {
    if (!onSort) return;
    if (sortBy !== sortValue) {
      onSort(sortValue, "asc");
    } else if (sortDirection === "asc") {
      onSort(sortValue, "desc");
    } else {
      onSort("", "asc");
    }
  };

  return (
    <Box
      sx={{
        position: "relative",
        borderRadius: edge
          ? {
              xs: 0,
              sm: ".75rem",
            }
          : ".75rem",
        paddingX: edge
          ? {
              xs: 0,
              sm: ".875rem",
            }
          : {
              xs: ".5rem",
              sm: ".875rem",
            },
      }}
    >
      {edge && (
        <Box
          sx={{
            display: {
              xs: "block",
              sm: "none",
            },
            position: "absolute",
            top: 0,
            right: 0,
            bottom: 0,
            width: "2.5rem",
            pointerEvents: "none",
            zIndex: 1,
            background:
              "linear-gradient(to right, var(--team-app-transparent-white), white)",
          }}
        />
      )}
      <TableContainer>
        <Table
          sx={{
            minWidth: "40rem",
            ...(edge && {
              borderRadius: {
                xs: 0,
                sm: "1rem",
              },
            }),
          }}
        >
          <TableHead>
            <TableRow>
              {props.headers.map((node, i) => { // NOSONAR: ReactNode headers have no stable identity; string headers use the value as key below
                const sortValue =
                  typeof node === "string"
                    ? sortableColumns?.[node]
                    : undefined;
                const isActive = !!sortValue && sortBy === sortValue;
                const width =
                  typeof node === "string" ? columnWidths?.[node] : undefined;
                return (
                  <TableCell
                    key={typeof node === "string" ? node : `header-${i}`}
                    variant="head"
                    sx={{
                      ...(width && { width }),
                      whiteSpace: "nowrap",
                      ...(edge
                        ? {
                            height: {
                              xs: "2.5rem",
                              sm: "auto",
                            },
                            paddingTop: {
                              xs: ".25rem",
                              sm: "1rem",
                            },
                            paddingBottom: {
                              xs: ".25rem",
                              sm: "1rem",
                            },
                          }
                        : undefined),
                    }}
                  >
                    {sortValue ? (
                      <Box
                        onClick={() => handleSortClick(sortValue)}
                        sx={{
                          display: "inline-flex",
                          alignItems: "center",
                          columnGap: ".375rem",
                          cursor: "pointer",
                          userSelect: "none",
                        }}
                      >
                        {node}
                        {!isActive ? (
                          <Box
                            sx={{
                              display: "inline-flex",
                              flexDirection: "column",
                              alignItems: "center",
                              lineHeight: 1,
                              opacity: 0.4,
                              gap: "1px",
                            }}
                          >
                            <FontAwesomeIcon
                              icon={faChevronUp}
                              className="text-[0.5rem]"
                            />
                            <FontAwesomeIcon
                              icon={faChevronDown}
                              className="text-[0.5rem]"
                            />
                          </Box>
                        ) : (
                          <FontAwesomeIcon
                            icon={
                              sortDirection === "asc"
                                ? faChevronUp
                                : faChevronDown
                            }
                            className="text-xs"
                          />
                        )}
                      </Box>
                    ) : (
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "row",
                          alignItems: "center",
                          columnGap: ".25rem",
                        }}
                      >
                        {node}
                        {showChevron(node, i) && (
                          <MediumCustomIcon icon="CHEVRONDOWN" />
                        )}
                      </Box>
                    )}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody
            sx={{
              "& tr:last-child td, & tr:last-child th": { border: 0 },
            }}
          >
            {props.rows}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};
